/**
 * AccessError Component
 *
 * Full-page error display with animated zebra mascot.
 * Features interactive eyes that follow the mouse cursor.
 *
 * Features:
 * - Large error code with gradient effect
 * - Animated zebra with blinking eyes
 * - Pupils follow mouse cursor
 * - "Surprised" state when mouse in corner
 * - Link back to home page
 *
 * Ported from Vue AccessError.vue
 *
 * @example
 * ```tsx
 * <AccessError
 *   code="404"
 *   message="This page does not exist."
 * />
 * ```
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { cx } from "@/utils/cx";
import type { AccessErrorProps } from "./types";
import styles from "./AccessError.module.css";
import zebraImage from "../../../assets/zebra.png";

export const AccessError: React.FC<AccessErrorProps> = ({
  code,
  message,
  homeUrl = "/",
  homeLinkText = "Go back home",
  className = "",
  "data-testid": testId,
}) => {
  const [isBlinking, setIsBlinking] = useState(false);
  const [isSurprised, setIsSurprised] = useState(false);
  const leftPupilRef = useRef<HTMLDivElement>(null);
  const rightPupilRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const blinkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Random blink function
  const triggerBlink = useCallback(() => {
    setIsBlinking(true);
    setTimeout(() => setIsBlinking(false), 150);
  }, []);

  // Use ref for scheduleNextBlink to avoid circular dependency
  const scheduleNextBlinkRef = useRef<() => void>(() => {});

  // Schedule next blink with random interval
  scheduleNextBlinkRef.current = () => {
    // Random interval between 1-5 seconds, or quick double blink
    const interval =
      Math.random() > 0.8
        ? 200 // Quick double blink
        : 1000 + Math.random() * 4000;

    blinkTimeoutRef.current = setTimeout(() => {
      triggerBlink();
      scheduleNextBlinkRef.current();
    }, interval);
  };

  // Start blinking on mount
  useEffect(() => {
    // Initial blink after a short delay
    const initialTimeout = setTimeout(() => {
      triggerBlink();
      scheduleNextBlinkRef.current();
    }, 500);

    return () => {
      clearTimeout(initialTimeout);
      if (blinkTimeoutRef.current) {
        clearTimeout(blinkTimeoutRef.current);
      }
    };
  }, [triggerBlink]);

  // Handle mouse movement
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const leftPupil = leftPupilRef.current;
    const rightPupil = rightPupilRef.current;

    if (!leftPupil || !rightPupil) return;

    // Update pupils to follow mouse
    const updatePupil = (pupil: HTMLDivElement) => {
      const rect = pupil.parentElement?.getBoundingClientRect();
      if (!rect) return;

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate angle from center to mouse
      const angle = Math.atan2(e.clientY - centerY, e.clientX - centerX);

      // Limit movement radius
      const maxOffset = 4;
      const offsetX = Math.cos(angle) * maxOffset;
      const offsetY = Math.sin(angle) * maxOffset;

      pupil.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
    };

    updatePupil(leftPupil);
    updatePupil(rightPupil);

    // Check if mouse is in top-left corner (surprised state)
    setIsSurprised(e.clientX < 100 && e.clientY < 100);
  }, []);

  // Build class names
  const containerClasses = cx(styles.container, className);
  const zebraClasses = cx(
    styles.zebra,
    isBlinking && styles.blinking,
    isSurprised && styles.surprised,
  );

  return (
    <div
      ref={containerRef}
      className={containerClasses}
      onMouseMove={handleMouseMove}
      data-testid={testId}
    >
      <div className={styles.content}>
        {/* Zebra mascot with animated eyes */}
        <div className={zebraClasses}>
          <img
            src={zebraImage}
            alt="Zebra mascot"
            className={styles.zebraImage}
          />

          {/* Eyes overlay */}
          <div className={styles.eyesContainer}>
            {/* Left eye */}
            <div className={styles.eyeWrapper}>
              <div className={styles.brow} />
              <div className={styles.eye}>
                <div
                  ref={leftPupilRef}
                  className={styles.pupil}
                  data-testid={testId ? `${testId}-left-pupil` : "left-pupil"}
                />
              </div>
            </div>

            {/* Right eye */}
            <div className={styles.eyeWrapper}>
              <div className={styles.brow} />
              <div className={styles.eye}>
                <div
                  ref={rightPupilRef}
                  className={styles.pupil}
                  data-testid={testId ? `${testId}-right-pupil` : "right-pupil"}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Error code */}
        <h1
          className={styles.code}
          data-testid={testId ? `${testId}-code` : "error-code"}
        >
          {code}
        </h1>

        {/* Error message */}
        <p
          className={styles.message}
          data-testid={testId ? `${testId}-message` : "error-message"}
        >
          {message}
        </p>

        {/* Home link */}
        <a
          href={homeUrl}
          className={styles.homeLink}
          data-testid={testId ? `${testId}-home-link` : "home-link"}
        >
          {homeLinkText}
        </a>
      </div>
    </div>
  );
};

AccessError.displayName = "AccessError";
